import { useCallback } from 'react';
import { Badge, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { LightbulbIcon } from '@shopify/polaris-icons';
import type { AiRecommendation, RecommendationLevel } from '../../api/recommendations';
import { useUpdateRecommendationStatus } from '../../hooks/useRecommendations';
import { IconBadge } from '../shared/IconBadge';
import { Pill } from '../shared/Pill';

interface Props {
  recommendation: AiRecommendation;
}

function levelTone(level: RecommendationLevel): 'critical' | 'warning' | 'success' {
  if (level === 'high') return 'critical';
  if (level === 'medium') return 'warning';
  return 'success';
}

function statusTone(status: string): 'success' | 'new' | undefined {
  return status === 'applied' ? 'success' : 'new';
}

export function RecommendationCard({ recommendation: rec }: Props) {
  const { mutate: updateStatus, isPending } = useUpdateRecommendationStatus();

  const handleApply = useCallback(() => {
    updateStatus({ id: rec.id, status: 'applied' });
  }, [rec.id, updateStatus]);

  const handleDismiss = useCallback(() => {
    updateStatus({ id: rec.id, status: 'dismissed' });
  }, [rec.id, updateStatus]);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" wrap={false} gap="300">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <IconBadge icon={LightbulbIcon} color={rec.priority === 'high' ? 'orange' : 'indigo'} />
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">{rec.title}</Text>
              <InlineStack gap="150" wrap>
                <Pill tone={levelTone(rec.impact)}>{rec.impact_label} impact</Pill>
                <Badge>{`Effort: ${rec.effort_label}`}</Badge>
                <Badge tone="new">{rec.audit_type}</Badge>
              </InlineStack>
            </BlockStack>
          </InlineStack>
        </InlineStack>

        <Text as="p" variant="bodySm">{rec.content}</Text>

        <InlineStack align="space-between" blockAlign="center">
          {rec.is_actionable ? (
            <InlineStack gap="200">
              <Button
                variant="primary"
                size="slim"
                onClick={handleApply}
                loading={isPending}
              >
                Take Action
              </Button>
              <Button
                variant="plain"
                tone="critical"
                size="slim"
                onClick={handleDismiss}
                loading={isPending}
              >
                Dismiss
              </Button>
            </InlineStack>
          ) : (
            <Badge tone={statusTone(rec.status)}>{rec.status_label}</Badge>
          )}

          {rec.cost_usd > 0 && (
            <Text as="span" variant="bodySm" tone="subdued">
              ${rec.cost_usd.toFixed(6)}
            </Text>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
